import { Component, OnInit, OnDestroy, inject, signal, computed } from '@angular/core';
import { CommonModule } from '@angular/common';
import { Router } from '@angular/router';
import { FormsModule } from '@angular/forms';
import { Subject, Subscription } from 'rxjs';
import { debounceTime, switchMap } from 'rxjs/operators';
import { GroupService } from '../../../services/group.service';
import { ProfileService } from '../../../services/profile.service';
import { Group } from '../../../dtos/group';
import { UserInfo } from '../../../dtos/user-info';
import { PaginatedResponse } from '../../../dtos/paginated';

const PAGE_SIZE = 15;
const DEBOUNCE_MS = 300;

@Component({
  selector: 'app-groups-page',
  standalone: true,
  imports: [CommonModule, FormsModule],
  templateUrl: './groups-page.component.html',
})
export class GroupsPageComponent implements OnInit, OnDestroy {
  private groupService = inject(GroupService);
  private profileService = inject(ProfileService);
  private router = inject(Router);

  readonly groupsPage = signal<PaginatedResponse<Group> | null>(null);
  readonly searchQuery = signal('');
  readonly currentPage = signal(0);
  readonly totalPages = signal(0);
  readonly loading = signal(true);
  readonly searchActive = signal(false);
  readonly errorMessage = signal('');
  private readonly search$ = new Subject<{ q: string; page: number }>();
  private userInfo: UserInfo | null = null;
  private sub?: Subscription;

  readonly groups = computed(() => this.groupsPage()?.items ?? []);
  readonly totalItems = computed(() => this.groupsPage()?.totalItems ?? 0);

  ngOnInit(): void {
    const searchSub = this.search$
      .pipe(
        debounceTime(DEBOUNCE_MS),
        switchMap(({ q, page }) => {
          this.currentPage.set(page);
          return this.groupService.getGroupsPaginated(page, PAGE_SIZE, q || undefined);
        }),
      )
      .subscribe({
        next: (res) => {
          this.groupsPage.set(res);
          this.totalPages.set(res.totalPages);
          this.currentPage.set(res.currentPage);
          this.loading.set(false);
          this.errorMessage.set('');
        },
        error: (err) => {
          this.errorMessage.set(err?.error?.message ?? 'Could not load groups.');
          this.loading.set(false);
        },
      });

    this.sub = new Subscription();
    this.sub.add(searchSub);

    this.loadData();

    this.profileService.getCurrentUser().subscribe({
      next: (user) => (this.userInfo = user),
      error: () => (this.userInfo = null),
    });
  }

  ngOnDestroy(): void {
    this.sub?.unsubscribe();
  }

  private loadData(): void {
    this.loading.set(true);
    this.errorMessage.set('');
    this.groupService.getGroupsPaginated(0, PAGE_SIZE).subscribe({
      next: (res) => {
        this.groupsPage.set(res);
        this.totalPages.set(res.totalPages);
        this.currentPage.set(res.currentPage);
        this.loading.set(false);
      },
      error: (err) => {
        this.errorMessage.set(err?.error?.message ?? 'Could not load groups.');
        this.loading.set(false);
      },
    });
  }

  private triggerSearch(): void {
    this.search$.next({ q: this.searchQuery(), page: this.currentPage() });
  }

  onSearchInput(value: string): void {
    this.searchQuery.set(value);
    this.searchActive.set(value.trim().length > 0);
    this.currentPage.set(0);
    this.triggerSearch();
  }

  clearSearch(): void {
    this.searchQuery.set('');
    this.searchActive.set(false);
    this.currentPage.set(0);
    this.triggerSearch();
  }

  goToPage(page: number): void {
    if (page < 0 || page >= this.totalPages()) return;
    this.currentPage.set(page);
    this.triggerSearch();
  }

  canPrev(): boolean {
    return this.currentPage() > 0;
  }

  canNext(): boolean {
    return this.currentPage() < this.totalPages() - 1;
  }

  isOwner(group: Group): boolean {
    return !!this.userInfo?.id && String(group.ownerUserId) === String(this.userInfo.id);
  }

  openGroup(group: Group): void {
    this.router.navigate(['/groups', group.id]);
  }
}
